import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle, XCircle, Loader2, ArrowLeft, Home, Tent, Mountain, Trees } from "lucide-react";

const Success = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");

  const [isSuccess, setIsSuccess] = useState(null); 
  const [statusMessage, setStatusMessage] = useState("Verifying your subscription...");
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (!sessionId) {
      setIsSuccess(false);
      setStatusMessage("Missing session ID.");
      return;
    }

    const verifySubscription = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8087";
        console.log("Using API URL:", apiUrl);


        const response = await axios.post(
          `${apiUrl}/verify-subscription`,
          { session_id: sessionId }
        );
        console.log("⚙️ Current build API URL:", process.env.REACT_APP_API_URL);



        const data = response.data;

        // Debug logs
        console.log("✅ Response from /verify-subscription:", response);

        if (data && typeof data.success === "boolean") {
          if (data.success) {
            setIsSuccess(true);
            setStatusMessage("🎉 Payment Successful! Subscription is active.");
            setEmail(data.email || "");
          } else {
            const reason = typeof data.reason === "string" ? data.reason : "User not subscribed";
            setIsSuccess(false);
            setStatusMessage(reason);
          }
        } else {
          setIsSuccess(false);
          setStatusMessage("Unexpected response format.");
        }
      } catch (error) {
        console.error("❌ Error verifying subscription:", error);
        setIsSuccess(false);
        setStatusMessage("Verification failed due to a server error.");
      }
    };

    verifySubscription();
  }, [sessionId]);

  const handleReturnToLogin = () => {
    navigate("/login");
  };

  const handleGoHome = () => {
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated background elements matching landing page */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
        <div className="absolute top-3/4 right-1/4 w-96 h-96 bg-teal-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-1/4 left-1/2 w-96 h-96 bg-cyan-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-500"></div>
      </div>

      <div className="relative z-10 max-w-2xl mx-auto w-full">
        <div className="bg-white/80 backdrop-blur-lg border border-white/20 rounded-3xl p-8 shadow-2xl text-center">
          {/* Header with brand elements */}
          <div className="flex items-center justify-center space-x-4 mb-8">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-3 rounded-2xl shadow-lg">
              <Tent className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              CampSite Checker
            </h2>
          </div>

          {/* Loading State */}
          {isSuccess === null && (
            <div className="space-y-6">
              <div className="relative">
                <Loader2 className="text-emerald-600 text-8xl mx-auto animate-spin" />
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full opacity-20 animate-ping"></div>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Verifying Your Payment
              </h1>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-600 animate-pulse rounded-full"></div>
              </div>
              <p className="text-gray-600">Please wait while we confirm your subscription...</p>
              
              {/* Nature icons for loading */}
              <div className="flex items-center justify-center space-x-4 mt-6">
                <Mountain className="w-8 h-8 text-emerald-600 animate-bounce" />
                <Trees className="w-8 h-8 text-teal-600 animate-bounce delay-100" />
                <Mountain className="w-8 h-8 text-emerald-600 animate-bounce delay-200" />
              </div>
            </div>
          )}

          {/* Success State */}
          {isSuccess === true && (
            <div className="space-y-6">
              <div className="relative">
                <CheckCircle className="text-emerald-600 text-8xl mx-auto animate-bounce" />
                <div className="absolute inset-0 bg-emerald-400 rounded-full opacity-20 animate-ping"></div>
              </div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-4">
                {typeof statusMessage === "string" ? statusMessage : "❌ Invalid status message"}
              </h1>
              {email && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 backdrop-blur-sm">
                  <p className="text-gray-700">
                    Subscription linked to: <span className="font-bold text-emerald-600">{email}</span>
                  </p>
                </div>
              )}
              
              {/* Success message */}
              <div className="bg-white/50 border border-emerald-200 rounded-2xl p-6 backdrop-blur-sm">
                <p className="text-gray-700 mb-4">
                  🎉 Welcome to CampSite Checker Premium! You now have access to:
                </p>
                <ul className="text-left space-y-2 text-gray-600">
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    <span>Real-time campsite availability</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    <span>Advanced search filters</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    <span>Priority customer support</span>
                  </li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-6">
                <button
                  onClick={handleReturnToLogin}
                  className="flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-semibold py-3 px-6 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  <ArrowLeft size={20} />
                  Return to Login
                </button>
                <button
                  onClick={handleGoHome}
                  className="flex items-center justify-center gap-2 border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50 font-semibold py-3 px-6 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  <Home size={20} />
                  Go Home
                </button>
              </div>
            </div>
          )}

          {/* Error State */}
          {isSuccess === false && (
            <div className="space-y-6">
              <div className="relative">
                <XCircle className="text-red-500 text-8xl mx-auto" />
                <div className="absolute inset-0 bg-red-400 rounded-full opacity-20 animate-pulse"></div>
              </div>
              <h1 className="text-4xl font-bold text-red-500 mb-4">
                {typeof statusMessage === "string" ? statusMessage : "❌ Invalid status message"}
              </h1>
              
              <div className="bg-red-50 border border-red-200 rounded-2xl p-4 backdrop-blur-sm">
                <p className="text-red-700">
                  Don't worry! No charges were made to your account. You can try again or contact our support team for assistance.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-6">
                <button
                  onClick={() => window.location.reload()}
                  className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold py-3 px-6 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  Try Again
                </button>
                <button
                  onClick={handleReturnToLogin}
                  className="flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-semibold py-3 px-6 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  <ArrowLeft size={20} />
                  Return to Login
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Success;